import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, HeartPulse, Compass, Shield, ArrowRight, Download, Check } from "lucide-react";
import { PROGRAM_BY_SLUG } from "../lib/signaturePrograms";

export default function StrongMinds() {
  const p = PROGRAM_BY_SLUG["change-choices"];
  const [prev, mental, lead] = p.curriculum;

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="strong-minds-hero">
        <div className="noise" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B21A8]/40 via-transparent to-transparent" />
        <div className="container-x relative py-24 md:py-32 grid gap-10 md:grid-cols-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="md:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">Signature Program · Ages 10–17 · 24 Weeks</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight">
              Change Choices:<br />
              <span className="italic text-[#F5B90A]">Strong Minds.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/80">
              {p.tagline} An integrated 24-week youth mental wellness, substance-use prevention, and
              healthy decision-making program.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/enroll" data-testid="sm-cta-enroll" className="btn-gold">Enroll Your Child <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/partnerships" data-testid="sm-cta-school" className="btn-outline-white">Request for Your School</Link>
              <Link to="/donate" data-testid="sm-cta-sponsor" className="btn-outline-white">Sponsor This Program</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3 Pillars */}
      <section className="bg-white py-20 md:py-28" data-testid="sm-pillars">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="overline text-[#6B21A8]">Three Interconnected Pillars</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">Prevention. Wellness. Leadership.</h2>
            <p className="mt-4 text-black/70 text-lg">
              Every young man moves through all three pillars across 24 weeks. We teach, discuss, role-play, and build the habits
              that create a lifetime of healthy decisions.
            </p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              { icon: Shield, title: "Substance Use Prevention", items: prev.items, color: "#6B21A8" },
              { icon: HeartPulse, title: "Mental & Emotional Wellness", items: mental.items, color: "#D4AF37" },
              { icon: Compass, title: "Leadership & Decision-Making", items: lead.items, color: "#111111" },
            ].map((pillar, i) => (
              <motion.div key={pillar.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="card-editorial p-8"
                data-testid={`sm-pillar-${i}`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md" style={{ backgroundColor: `${pillar.color}15`, color: pillar.color }}>
                    <pillar.icon className="h-6 w-6" strokeWidth={2} />
                  </div>
                  <h3 className="font-display text-xl font-bold">{pillar.title}</h3>
                </div>
                <ul className="mt-6 space-y-2">
                  {pillar.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-black/80">
                      <Check className="h-4 w-4 mt-0.5 text-[#6B21A8] flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#F7F3EC] py-20 md:py-28" data-testid="sm-how-it-works">
        <div className="container-x grid gap-14 md:grid-cols-12">
          <div className="md:col-span-5">
            <span className="overline text-[#6B21A8]">How It Works</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">24 weeks of real conversations and real skills.</h2>
            <p className="mt-5 text-black/70 text-lg">
              {p.experience}
            </p>
          </div>
          <div className="md:col-span-7 grid gap-4">
            {[
              { n: "01", title: "Interactive Lessons", body: "Age-appropriate curriculum with discussion, media, and role-play." },
              { n: "02", title: "Scenario Work", body: "Real situations young men face — peer pressure, grief, conflict, choices." },
              { n: "03", title: "Mentor Guidance", body: "Trained mentors facilitate every circle and follow up one-on-one." },
              { n: "04", title: "Family Participation", body: "Monthly family nights and take-home guides reinforce what we cover." },
              { n: "05", title: "Professional Referrals", body: "When extra support is needed, we help families find qualified community resources." },
            ].map((s) => (
              <div key={s.n} className="flex gap-5 rounded-lg bg-white p-6 border border-black/5" data-testid={`sm-step-${s.n}`}>
                <span className="font-display text-3xl font-bold text-[#F5B90A]">{s.n}</span>
                <div>
                  <h4 className="font-display text-lg font-bold">{s.title}</h4>
                  <p className="mt-1 text-sm text-black/70">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-[#0A0A0A] text-white py-14" data-testid="sm-disclaimer">
        <div className="container-x">
          <div className="mx-auto max-w-3xl rounded-lg border border-white/15 p-8 text-center">
            <div className="flex items-center justify-center gap-3">
              <Brain className="h-6 w-6 text-[#F5B90A]" />
              <p className="overline text-[#F5B90A]">Important</p>
            </div>
            <p className="mt-4 text-white/85">{p.disclaimer}</p>
            <Link to="/strong-minds-center" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#F5B90A] hover:underline">
              Visit the Strong Minds Resource Center <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20" data-testid="sm-final-cta">
        <div className="container-x text-center max-w-3xl mx-auto">
          <h2 className="font-display text-4xl md:text-5xl font-bold">Strong minds build brighter futures.</h2>
          <p className="mt-4 text-black/70 text-lg">Enroll your child, bring the program to your school, or sponsor a cohort today.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/enroll" className="btn-primary" data-testid="sm-footer-enroll"><Download className="h-4 w-4" /> Enroll Your Child</Link>
            <Link to="/partnerships" className="btn-outline" data-testid="sm-footer-partner">Partner With Us</Link>
            <Link to="/donate" className="btn-gold" data-testid="sm-footer-donate">Sponsor This Program</Link>
          </div>
        </div>
      </section>
    </>
  );
}
